import React, { useEffect, useState } from 'react';
import FormField from './FormField';
import {
    Autocomplete,
    Box,
    Chip,
    Tooltip,
    CircularProgress,
    Stack,
    Switch,
    Typography,
    MenuItem,
    TextField,
    Grid,
    Button,
    Divider
} from '@mui/material';
import { equalShallowCheck} from '../../utils/formUtils'
import { formatDate } from '../../utils/dateFormatter'
import { SectionAccordion } from '../accordions/SectionAccordion';
import FormActionsBar from "../../components/formActionsBar/FormActionsBar";
import PermissionsCategoryAccordion from '../../components/accordions/PermissionsCategoryAccordion'
import DocumentTypePermissionsGrid from '../../components/grids/DocumentTypePermissionsGrid'

function UserGroupForm({
    //settings
    mode = 'create',
    lockedLevelByKey=[],
    
    // data
    initialValues = null,
    availableLanguages= [],
    availableX3Folders= [],
    availablePermissionCategories=[],
    permissionLevelDefinitions = [],
    permissionLevels =[],
    documentTypes= [],
    docTypeActionsConfig = [],
    docTypePresetsConfig = [],

    //state
    isLoading= false,
    isLoadingAvailableX3Folders=false,
    isLoadingDocumentTypes=false,
    saving=false,
    
    // functions
    presetToActions,
    onDirtyChange,
    onSubmit
}){
    const isEditMode = mode === 'edit';

    const [formData, setFormData] = useState({
        id: 0,
        name: '',
        description: '',
        language: '',
        isActive: true,
        memberIds: [],
        x3Folders: [],
        lastUpdated: [],
        permissionLevels: {},
        documentTypePermissions: {},
        audit: {
            createdBy: 0,
            createdByName: '',
            createdOn: '',
            modifiedBy: 0,
            modifiedByName: '',
            modifiedOn: ''
        }
    });

    const [errors, setErrors] = useState({});
    const [initialSnapshot, setInitialSnapshot] = useState(null);
    const [isDirty, setIsDirty] = useState(false);

    useEffect(() => {
        if (!initialValues) return;

        const originalSnapshotBase = {
            id: initialValues?.id || initialValues._id,
            name: initialValues?.name || '',
            description: initialValues?.description || '',
            language: initialValues?.language || '',
            isActive: initialValues.isActive,
            memberIds: initialValues.memberIds,
            x3Folders: initialValues.x3Folders,
            lastUpdated: initialValues.lastUpdated,
            permissionLevels: initialValues.permissionLevels,
            documentTypePermissions: initialValues.documentTypePermissions,
            audit: {
                createdBy: initialValues?.audit.createdBy,
                createdByName: initialValues?.audit.createdByName,
                createdOn: initialValues?.audit.createdOn,
                modifiedBy: initialValues?.audit.modifiedBy,
                modifiedByName: initialValues?.audit.modifiedByName,
                modifiedOn: initialValues?.audit.modifiedOn
            }
        }

        setFormData(originalSnapshotBase);
        setInitialSnapshot(originalSnapshotBase);
        setIsDirty(false);
        onDirtyChange && onDirtyChange(false);

    }, [initialValues, onDirtyChange]);

    useEffect(() => {
        if (!isEditMode) return;
        if (!initialValues) return;

        if (!availableX3Folders || availableX3Folders.length === 0) return;
    
        const currentX3Folders = initialValues.x3Folders|| []; 
    
        const mappedX3Folders = availableX3Folders
        .filter(availableItem => currentX3Folders.some(currentItem => currentItem.id === availableItem.id)
        )
        .map(item => ({
            id: item.name,
            name: item.name
        }));
        
        const flatFolderIds = mappedX3Folders.map(item => item.id);
        setFormData(prev => {
          const next = {
            ...prev,
            x3Folders: flatFolderIds
          };

          setInitialSnapshot(next);
          // Don't call updateDirtyFlag here - this is initial setup, not a user change
          setIsDirty(false);
          if (onDirtyChange) onDirtyChange(false);
          return next;
        });
    
    }, [isEditMode, initialValues, availableX3Folders, onDirtyChange]);

    useEffect(() => {
        if (!availableLanguages?.length) return;

        setFormData((prev) => {
            if (prev.language) return prev;
            return { ...prev, language: availableLanguages[0] };
        });
    }, [availableLanguages]);


    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        setFormData((prev) => {
            const next = {
                ...prev,
                [name]: type === 'checkbox' ? checked : value,
            };
            updateDirtyFlag(next);
                return next;
        });
    };

    const handleX3FolderChange = (event, selectedOptions) => {
        const safeOptions = selectedOptions || [];
        const ids = safeOptions.map(opt => opt.id);
        setFormData((prev) => {
            const next = {
                ...prev,
                x3Folders : ids
            };

            updateDirtyFlag(next);
            return next;
        })
    };

    const handleDocTypePermsChange = (nextPermissions) => {
        setFormData((prev) => {
        const next = {
            ...prev,
            documentTypePermissions: nextPermissions,
        };
        updateDirtyFlag(next);
        return next;
        });
    };

    const updateDirtyFlag = (nextForm) => {
        if (!initialSnapshot) return;
        const dirty = !equalShallowCheck(initialSnapshot, nextForm);
        setIsDirty(dirty);
        if (onDirtyChange) onDirtyChange(dirty);
    };

    const validate = () => {
        let errors = {};

        if (!formData.name || !formData.name.trim()) {
            errors.name = 'Name is requiered.';
        }

        if (!formData.language || !formData.language.trim()) {
            errors.language = 'Language is requiered.';
        }

        if (!formData.description || !formData.description.trim()) {
            errors.description = 'Description is required.';
        }

        setErrors(errors);

        return Object.keys(errors).length === 0;
    }

    const handleSubmitInternal = (e) => {
        e.preventDefault();
        if (!validate()) return;
        if (onSubmit) {
            onSubmit(formData);
        }
    };

    const isBusy = isLoading || isLoadingAvailableX3Folders || isLoadingDocumentTypes;

    return (
    <form className="s-user-form" onSubmit={handleSubmitInternal}>
        <Stack spacing={0}>

        <SectionAccordion
            title='User group details'
            loading={isLoading && isEditMode}
            defaultExpanded
            disabled={isBusy}
        >
            <Stack sx={{ p: 2 }}>
                <Grid container spacing={2.5}>
                    <Grid item md={6} >
                        <FormField label="Name" required={false} error={errors.name}>
                            <TextField
                            name="name"
                                value={formData.name}
                                onChange={handleChange}
                                size="small"
                                fullWidth
                                placeholder="Group name"
                            />
                        </FormField>
                    </Grid>

                    <Grid item xs={12} md={6}>
                    <FormField label="Status" required={false} error={errors.isActive}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                        <Switch
                            name="isActive"
                            checked={formData.isActive}
                            onChange={handleChange}
                        />
                        <Typography variant="body2">
                            {formData.isActive ? "Active" : "Inactive"}
                        </Typography>
                        </Stack>
                    </FormField>
                    </Grid>

                    <Grid item xs={12}>
                    <FormField label="Description" required={false} error={errors.description}>
                        <TextField
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        size="small"
                        fullWidth
                        placeholder="Short description of what this group is for"
                        />
                    </FormField>
                    </Grid>

                    <Grid item xs={12}>
                    <FormField
                        label="X3 Folders"
                        required={false}
                        error={errors.availableX3Folders}
                    >
                        <Autocomplete
                        multiple
                        options={availableX3Folders}
                        getOptionLabel={(option) => option.name}
                        disableCloseOnSelect
                        disabled={isLoading || isLoadingAvailableX3Folders || isLoadingDocumentTypes}
                        fullWidth
                        value={availableX3Folders.filter((g) => formData.x3Folders.includes(g.id))}
                        onChange={handleX3FolderChange}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                            <Chip
                                {...getTagProps({ index })}
                                label={option.name}
                                variant="outlined"
                                size="small"
                                sx={{ borderRadius: 999 }}
                            />
                            ))
                        }
                        renderInput={(params) => (
                            <TextField
                            {...params}
                            variant="outlined"
                            size="small"
                            placeholder={
                                isLoadingAvailableX3Folders
                                ? "Loading X3 folders…"
                                : "Select folders or type to search"
                            }
                            />
                        )}
                        />
                    </FormField>
                    </Grid>
                    <Grid item xs={12} md={6}>
                    <FormField label="Language" required={false} error={errors.language}>
                        <TextField
                            select
                            name="language"
                            value={formData.language || ''}
                            onChange={handleChange}
                            size="small"
                            fullWidth
                            disabled={true}
                        >
                            {availableLanguages.length === 0 ? (
                                <MenuItem value="" disabled>
                                Loading…
                                </MenuItem>
                            ) : (
                                availableLanguages.map((lang) => (
                                <MenuItem key={lang} value={lang}>
                                    {lang}
                                </MenuItem>
                                ))
                            )}
                        </TextField>
                    </FormField>
                    </Grid>
                </Grid>
                </Stack>
        </SectionAccordion>

        <SectionAccordion
            title='Permissions (Site Access)'
            loading={isLoading || isLoadingAvailableX3Folders || isLoadingDocumentTypes}
            disabled={isBusy}
        >
            <Box sx={{ p: 1 }}>
                {availablePermissionCategories.map((cat) => (
                    <PermissionsCategoryAccordion
                    key={cat.id}
                    title={cat.title}
                    subTitle={cat.subTitle}
                    resources={cat.resources}
                    value={formData.permissionLevels}
                    disabled={isLoading || isLoadingAvailableX3Folders || isLoadingDocumentTypes}
                    onChange={(updater) =>
                        setFormData((prev) => {
                        const next = {
                            ...prev,
                            permissionLevels:
                            typeof updater === "function"
                                ? updater(prev.permissionLevels ?? {})
                                : updater,
                        };
                        updateDirtyFlag(next);
                        return next;
                        })
                    }
                    defaultExpanded={cat.id === "core"}
                    permissionLevelDefinitions={permissionLevelDefinitions}
                    permissionLevels={permissionLevels}
                    lockedLevelByKey={lockedLevelByKey}
                    documentTypePermissions={formData.documentTypePermissions}
                    />
                ))}
            </Box>
        </SectionAccordion>

        <SectionAccordion
            title='Document Type Permissions (Document Permissions)'
            loading={((isLoading || isLoadingDocumentTypes) && isEditMode)}
            disabled={isBusy}
        >
            <Box sx={{ p: 1 }}>
            <DocumentTypePermissionsGrid
                documentTypes={documentTypes}
                folders={formData.x3Folders}
                documentTypePermissions={formData.documentTypePermissions}
                docTypeActionsConfig={docTypeActionsConfig}
                docTypePresetsConfig={docTypePresetsConfig}
                presetToActions={presetToActions}
                onChange={handleDocTypePermsChange}
            />
            </Box>
        </SectionAccordion>

        {isEditMode && formData.audit && (
            <>
            <Divider sx={{ mt: 3, mb: 1 }} />
            <Stack spacing={0.5} sx={{ px: 1 }}>
                <Typography variant="subtitle2">Audit</Typography>
                <Typography variant="body2" color="text.secondary">
                    Created by: <strong>{formData.audit?.createdByName || "—"}</strong>{" "}
                    ({formData.audit?.createdOn ? String(formatDate(formData.audit.createdOn)) : "—"})
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Modified by: <strong>{formData.audit?.modifiedByName || "—"}</strong>{" "}
                    ({formData.audit?.modifiedOn ? String(formatDate(formData.audit.modifiedOn)) : "—"})
                </Typography>
            </Stack>
            </>
        )}

        <FormActionsBar>
            <Tooltip title={isEditMode && !isDirty ? "No changes to save" : ""}>
                <span>
                <Button
                    type="submit"
                    variant="contained"
                    sx={{ textTransform: "none", minWidth: 160 }}
                    disabled={isBusy || (isEditMode && !isDirty)}
                    startIcon={saving ? <CircularProgress size={18} color="inherit" /> : null}
                >
                    {isEditMode ? "Save changes" : "Create"}
                </Button>
                </span>
            </Tooltip>
        </FormActionsBar>
        </Stack>
    </form>
);
}

export default UserGroupForm;